import { useCallback, useEffect, useRef, useState } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BackButton } from "./back-button";
import { useMagicMomentStore } from "./use-magic-moment-store";

interface MagicMomentDrawerProps {
  teenGender: "male" | "female" | null;
  teenId?: string | null;
  onInsertToChat?: (text: string) => void;
}

type Token = string | { id: string; placeholder: string };

const FEMALE_TOKENS: Token[] = [
  "hiii! been thinking about ",
  { id: "thing", placeholder: "specific thing from your convo" },
  " and ",
  { id: "why", placeholder: "why it stuck with you" },
  ". feeling really lucky i got matched as ur mentor. ",
  { id: "compliment", placeholder: "give them a genuine compliment" },
  " so excited to keep getting to know each other!",
];

const MALE_TOKENS: Token[] = [
  "yooo! been thinking about what u said about ",
  { id: "thing", placeholder: "specific thing" },
  " and ",
  { id: "why", placeholder: "why it stuck with you" },
  ". honestly feeling lucky i got matched as ur mentor. ",
  { id: "compliment", placeholder: "give them a genuine compliment" },
  " excited to keep getting to know u!",
];

interface FillInProps {
  placeholder: string;
  onChange: (value: string) => void;
}

function FillIn({ placeholder, onChange }: FillInProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [filled, setFilled] = useState(false);

  // Treat the bracketed placeholder as the "empty" state. If the mentor's
  // current text equals the bracketed placeholder string, it's still empty.
  const handleInput = useCallback(() => {
    const text = ref.current?.textContent ?? "";
    const isPlaceholder = text === `[${placeholder}]`;
    const nextValue = isPlaceholder ? "" : text;
    setFilled(!isPlaceholder && text.length > 0);
    onChange(nextValue);
  }, [placeholder, onChange]);

  const handleFocus = () => {
    const node = ref.current;
    if (!node) return;
    const text = node.textContent ?? "";
    if (text === `[${placeholder}]`) {
      // Clear the placeholder text on first focus so the mentor can type.
      node.textContent = "";
      setFilled(false);
      // Place the caret inside the now-empty span so typing starts immediately.
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleBlur = () => {
    const text = ref.current?.textContent ?? "";
    if (text.trim().length === 0) {
      if (ref.current) {
        ref.current.textContent = `[${placeholder}]`;
      }
      setFilled(false);
      onChange("");
    }
  };

  return (
    <span
      ref={ref}
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(
        "inline-block min-w-[1ch] border-b-2 px-0.5 pb-0.5 outline-none focus:border-[#1f1e1d]",
        filled
          ? "border-[#1f1e1d] text-[#1f1e1d]"
          : "border-[#dfdad3] text-[#999388]",
      )}
    >
      {`[${placeholder}]`}
    </span>
  );
}

export function MagicMomentDrawer({
  teenGender,
  teenId,
  onInsertToChat,
}: MagicMomentDrawerProps) {
  const { track } = useTrackEvent();
  const viewTracked = useRef(false);
  const goBack = useMagicMomentStore((s) => s.goBack);

  const tokens = teenGender === "male" ? MALE_TOKENS : FEMALE_TOKENS;
  const valuesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!viewTracked.current) {
      viewTracked.current = true;
      track(PostHogEvents.DAY2_MAGIC_MOMENT_VIEW, {
        teen_id: teenId,
        template_variant: teenGender,
      });
    }
  }, []);

  const handleBack = () => {
    track(PostHogEvents.DAY2_MAGIC_MOMENT_BACK, { teen_id: teenId });
    goBack();
  };

  const handleInsert = () => {
    const built = tokens
      .map((t) => {
        if (typeof t === "string") return t;
        const value = valuesRef.current[t.id]?.trim();
        return value && value.length > 0 ? value : `[${t.placeholder}]`;
      })
      .join("");
    onInsertToChat?.(built);
  };

  const setValue = useCallback((id: string, value: string) => {
    valuesRef.current[id] = value;
  }, []);

  return (
    <div className="rounded-t-[16px] border-x border-t border-[#ece8e1] bg-[#f7f5f1] px-[17px] pb-4 pt-[17px]">
      <BackButton onClick={handleBack} />
      <div className="flex flex-col gap-1">
        <h3 className="text-[16px] font-bold leading-[1.2] tracking-[-0.16px] text-[#1f1e1d]">
          Magic moment
        </h3>
        <p className="text-[14px] leading-[1.4] text-[#58534c]">
          One thoughtful message can make a teen feel really seen. Use this as a
          starting point and make it yours.
        </p>
      </div>
      <div className="mt-2 select-text rounded-[16px] bg-white p-4 text-[14px] leading-[1.4] text-[#1f1e1d] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
        <p>
          {tokens.map((token, i) => {
            if (typeof token === "string") {
              return <span key={`t-${i}`}>{token}</span>;
            }
            return (
              <FillIn
                key={`${teenGender}-${token.id}`}
                placeholder={token.placeholder}
                onChange={(value) => setValue(token.id, value)}
              />
            );
          })}
        </p>
      </div>
      {onInsertToChat && (
        <Button
          variant="ghost"
          onClick={handleInsert}
          className="mt-3 h-auto p-0 text-[14px] font-bold leading-[1.4] text-[#1f1e1d] hover:bg-transparent hover:text-[#58534c]"
        >
          Insert message
        </Button>
      )}
    </div>
  );
}
